import { useEffect, useState } from 'react';
import { apiFetch } from '../api/client';

export type EditablePreset = { id: string; label: string; prompt: string; version?: number; [key: string]: unknown };

export function PromptPresetEditor({ kind, selected, prompt, onPromptChange, onSaved, onDeleted }: {
  kind: 'content' | 'style'; selected?: EditablePreset; prompt: string;
  onPromptChange: (value: string) => void; onSaved: (preset: EditablePreset) => void;
  onDeleted: (id: string) => void;
}) {
  const [label, setLabel] = useState('');
  const [category, setCategory] = useState('content');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  useEffect(() => { setLabel(selected?.label || ''); setCategory(selected?.category === 'commerce' ? 'commerce' : 'content'); setMessage(''); }, [selected?.id]);
  const save = async (asNew: boolean) => {
    if (!label.trim() || !prompt.trim()) { setMessage('กรุณากรอกชื่อและ Prompt'); return; }
    setBusy(true); setMessage('');
    try {
      const id = asNew ? crypto.randomUUID() : selected?.id;
      if (!id) throw new Error('เลือก preset ก่อนแก้ไข');
      const data = { ...selected, id, label: label.trim(), prompt: prompt.trim(), desc: prompt.trim(), ...(kind === 'content' ? { category, badge: label.trim() } : {}) };
      const response = await apiFetch(`/api/prompt-presets/${encodeURIComponent(id)}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', 'Idempotency-Key': crypto.randomUUID() },
        body: JSON.stringify({ kind, version: asNew ? 0 : selected?.version || 0, data }),
      });
      const result = await response.json();
      onSaved({ ...data, version: result.version });
      setMessage('บันทึก preset ในระบบแล้ว (ไม่เปลี่ยนงานที่สร้างไว้ก่อนหน้า)');
    } catch (error) { setMessage(error instanceof Error ? error.message : 'บันทึกไม่สำเร็จ'); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!selected || !window.confirm(`ลบ Preset "${selected.label}"? งานที่สร้างไว้แล้วจะไม่เปลี่ยนแปลง`)) return;
    setBusy(true); setMessage('');
    try {
      await apiFetch(`/api/prompt-presets/${encodeURIComponent(selected.id)}`, {
        method: 'DELETE', headers: { 'Content-Type': 'application/json', 'Idempotency-Key': crypto.randomUUID() },
        body: JSON.stringify({ kind, version: selected.version || 0 }),
      });
      onDeleted(selected.id);
    } catch (error) { setMessage(error instanceof Error ? error.message : 'ลบไม่สำเร็จ'); }
    finally { setBusy(false); }
  };
  return <div className="space-y-2 rounded-xl border border-slate-700 p-3">
    {kind === 'content' && <label className="block text-xs text-slate-300">หมวดของ Preset
      <select value={category} onChange={(e) => setCategory(e.target.value)} className="ml-2 rounded bg-slate-900 p-2">
        <option value="content">เนื้อหา/เรื่องเล่า</option><option value="commerce">สินค้า/การตลาด</option>
      </select>
    </label>}
    <label className="block text-xs text-slate-300">{kind === 'content' ? 'Content Prompt — โครงเรื่องและวิธีนำเสนอ' : 'Style Prompt — รูปลักษณ์ภาพเท่านั้น'}
      <textarea value={prompt} onChange={(e) => onPromptChange(e.target.value)} rows={4} className="mt-2 w-full rounded-lg bg-slate-900 p-2 text-sm" />
    </label>
    <label className="block text-xs text-slate-300">ชื่อ Preset
      <input value={label} onChange={(e) => setLabel(e.target.value)} className="mt-1 w-full rounded-lg bg-slate-900 p-2 text-sm" />
    </label>
    <div className="flex gap-3 text-xs">
      <button type="button" disabled={busy || !selected} onClick={() => save(false)} className="rounded bg-sky-700 p-2 disabled:opacity-50">บันทึกแก้ไข Preset เดิม</button>
      <button type="button" disabled={busy} onClick={() => save(true)} className="rounded bg-slate-700 p-2 disabled:opacity-50">สร้าง Preset ใหม่</button>
      <button type="button" disabled={busy || !selected} onClick={remove} className="rounded bg-rose-900 p-2 disabled:opacity-50">ลบ Preset</button>
    </div>
    <p className="text-xs text-slate-400">แก้ Prompt เพื่อใช้กับ Job นี้ได้โดยไม่ต้องบันทึก Preset</p>
    {message && <p role="status" className="text-xs text-amber-300">{message}</p>}
  </div>;
}
